import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Mail, UserPlus, Send } from "lucide-react";

export default function Staff() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role_label: "Yardımcı Personel", phone: "", active: true });

  const load = async () => setItems((await api.get("/staff")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", role_label: "Yardımcı Personel", phone: "", active: true }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Ad ve e-posta zorunlu");
    try {
      if (editing) await api.put(`/staff/${editing.id}`, form);
      else await api.post("/staff", form);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/staff/${id}`);
    toast.success("Silindi"); load();
  };

  const testEmail = async () => {
    try {
      const { data } = await api.post("/notifications/test");
      if (data.sent) toast.success("Test e-postası admin'e gönderildi");
      else toast.error("E-posta gönderilemedi");
    } catch { toast.error("Hata"); }
  };

  const scanNow = async () => {
    try {
      const { data } = await api.post("/reminders/scan-now");
      toast.success(`Tarama tamam: ${data.scanned} yaklaşan servis/muayene bulundu`);
    } catch { toast.error("Tarama başarısız"); }
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Erişim & Bildirim</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Personel</h1>
          <p className="mt-2 text-sm text-slate-500">
            Yardımcı personel listesi. Servis/muayene ve kritik araç bildirimleri buradaki herkese gider.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={scanNow} variant="outline" data-testid="scan-reminders-btn">
            <Send className="w-4 h-4 mr-2" /> Hatırlatmayı Şimdi Tara
          </Button>
          <Button onClick={openNew} data-testid="add-staff-btn" className="bg-[#1E3A8A] hover:bg-[#1e40af]">
            <UserPlus className="w-4 h-4 mr-2" /> Yeni Personel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <MiniStat label="Toplam Personel" value={items.length} />
        <MiniStat label="Aktif Bildirim Alan" value={items.filter((s) => s.active).length} color="text-emerald-600" />
        <MiniStat label="Pasif" value={items.filter((s) => !s.active).length} color="text-slate-400" />
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg">Bildirim Listesi</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>E-posta</TableHead>
                  <TableHead>Görev</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} className="hover:bg-slate-50">
                    <TableCell className="font-semibold flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-blue-50 text-[#1E3A8A] flex items-center justify-center">
                        <Mail className="w-4 h-4" />
                      </div>
                      {s.name}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{s.email}</TableCell>
                    <TableCell className="text-sm">{s.role_label || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{s.phone || "-"}</TableCell>
                    <TableCell>
                      {s.active
                        ? <span className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Aktif</span>
                        : <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500 border border-slate-200">Pasif</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Henüz personel eklenmemiş</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Personeli Düzenle" : "Yeni Personel"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider">Ad Soyad</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="staff-name-input" />
            </div>
            <div><Label className="text-xs uppercase tracking-wider">E-posta</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="staff-email-input" />
            </div>
            <div><Label className="text-xs uppercase tracking-wider">Görev / Rol Etiketi</Label>
              <Input value={form.role_label} onChange={(e) => setForm({ ...form, role_label: e.target.value })} className="mt-1" placeholder="Örn: Atölye Şefi" />
            </div>
            <div><Label className="text-xs uppercase tracking-wider">Telefon (opsiyonel)</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
              <span className="text-sm">Bildirim Al</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <Button onClick={save} data-testid="save-staff-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1e40af]">
              {editing ? "Güncelle" : "Kaydet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, color = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`metric-num text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
